/*
 * The pure rules of sending (spec 08 §6): how long to wait after a failed attempt, which failures
 * are worth another attempt, and which item of a target is next. Nothing here touches a socket or
 * a database, so the outbox's behaviour under every failure is a table test. What an item turns into
 * on the wire is its module's (`transport.ts`).
 */

import type { AttachmentRef as WireAttachmentRef } from "../wire/core.gen.ts"
import type { AttachmentRef, OutboxItem } from "./types.ts"

/** 1, 2, 4, 8 … capped at 30 s, jittered ±30 % (spec 08 §6.7). */
export const BACKOFF_BASE_MS = 1000
export const BACKOFF_CAP_MS = 30_000
export const BACKOFF_JITTER = 0.3

/** After this many transient failures in a row an item stops retrying and asks the user. */
export const MAX_TRANSIENT_ATTEMPTS = 10

/** Codes a repeat of the same frame can outlive; every other code is the server's final word. */
const RETRYABLE_CODES = new Set([
	"rateLimited",
	"unavailable",
	"internal",
	"protocol",
	"insufficientStorage",
])

export function backoffMs(attempts: number, random: () => number = Math.random): number {
	const exponent = Math.max(0, attempts - 1)
	const base = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** exponent)
	const jitter = 1 - BACKOFF_JITTER + random() * 2 * BACKOFF_JITTER
	return Math.round(base * jitter)
}

export interface Failure {
	key: string
	retryable: boolean
	retryAfterMs?: number
	/**
	 * The server set the item aside for its author to decide and wrote nothing (an AI chat's 409
	 * `crossed`): it is neither retried nor handed back to the draft, but kept for the author.
	 */
	needsAuthor?: boolean
}

/**
 * Reads a failed request. A `WireError` says itself whether it is retryable; a bare wire payload
 * is judged by its code; anything else — the socket closing mid-flight, a thrown string — is a
 * transport failure and is retried. A transport marks the one answer that is neither with
 * `needsAuthor: true` on what it throws.
 */
export function classify(err: unknown): Failure {
	if (typeof err !== "object" || err === null) return { key: "error.network", retryable: true }
	const e = err as {
		code?: unknown
		key?: unknown
		retryable?: unknown
		retryAfterMs?: unknown
		needsAuthor?: unknown
	}
	const code = typeof e.code === "string" ? e.code : undefined
	const key = typeof e.key === "string" && e.key !== "" ? e.key : `error.${code ?? "network"}`
	if (e.needsAuthor === true) return { key, retryable: false, needsAuthor: true }
	const failure: Failure = {
		key,
		retryable:
			typeof e.retryable === "boolean"
				? e.retryable
				: code === undefined || RETRYABLE_CODES.has(code),
	}
	if (typeof e.retryAfterMs === "number" && e.retryAfterMs > 0)
		failure.retryAfterMs = e.retryAfterMs
	return failure
}

/**
 * One staged attachment as the wire's `AttachmentRef` (core's, protocol.md §2.6): the id the upload
 * answered, and the two fields the sender may still have changed in the tray. Both are sent only
 * when they carry something — the server keeps the stored value for an empty one — so a plain
 * attachment travels as the single id it was before captions existed. Every wire that carries a
 * draft's files uses it: chat's send and edit, the draft mirror, an AI chat's prompt.
 */
export function wireAttachmentRef(a: AttachmentRef): WireAttachmentRef {
	const ref: WireAttachmentRef = { id: a.id }
	if (a.name !== "") ref.filename = a.name
	if (a.caption !== undefined && a.caption !== "") ref.caption = a.caption
	return ref
}

/** Items grouped by target, each group in send order. */
export function byTarget(items: readonly OutboxItem[]): Map<string, OutboxItem[]> {
	const groups = new Map<string, OutboxItem[]>()
	for (const item of items) {
		const group = groups.get(item.target) ?? []
		group.push(item)
		groups.set(item.target, group)
	}
	for (const group of groups.values()) group.sort((a, b) => a.order - b.order)
	return groups
}

/**
 * The head of each target that may go out at `now`, and the earliest moment anything else becomes
 * due. A failed head pauses its target and only its target (spec 08 §6.4), and so does one set aside
 * for its author — what was written after it waits for the author's answer; a held item whose
 * window has passed is due; an item created in the future — the clock moved backwards — is due at
 * once rather than never (spec 08 §6.10).
 */
export function due(
	items: readonly OutboxItem[],
	now: number,
): { ready: OutboxItem[]; nextAt: number | undefined } {
	const ready: OutboxItem[] = []
	let nextAt: number | undefined
	const consider = (at: number) => {
		if (at > now && (nextAt === undefined || at < nextAt)) nextAt = at
	}
	for (const group of byTarget(items).values()) {
		const head = group[0]
		if (
			head === undefined ||
			head.state === "failed" ||
			head.state === "inflight" ||
			head.state === "needsAuthor"
		) {
			continue
		}
		const at = dueAt(head)
		if (at <= now || now < head.createdAt) ready.push(head)
		else consider(at)
	}
	return { ready, nextAt }
}

function dueAt(item: OutboxItem): number {
	return item.state === "held" ? item.sendAfter : Math.max(item.sendAfter, item.nextAttemptAt)
}
